/**
 * aero-component-header-search.js
 * Header search modal — open/close, /search/suggest, keyboard navigation.
 * Loaded only via snippets/aero-component-header-search.liquid.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS = 300;
  var SUGGEST_LIMIT = 6;

  function trapFocus(container, focusTarget) {
    var focusables = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    function onKeyDown(event) {
      if (event.key !== 'Tab') return;
      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeyDown);
    (focusTarget || first).focus();

    return function release() {
      container.removeEventListener('keydown', onKeyDown);
    };
  }

  function initHeaderSearch(root) {
    var openBtn = root.querySelector('[data-aero-header-search-open]');
    var modal = root.querySelector('[data-aero-header-search-modal]');
    var overlay = root.querySelector('[data-aero-header-search-overlay]');
    var closeBtn = root.querySelector('[data-aero-header-search-close]');
    var input = root.querySelector('[data-aero-header-search-input]');
    var results = root.querySelector('[data-aero-header-search-results]');
    var status = root.querySelector('[data-aero-header-search-status]');
    var form = root.querySelector('[data-aero-header-search-form]');

    if (!openBtn || !modal || !input) return;

    var releaseFocus = null;
    var debounceTimer = null;
    var activeIndex = -1;
    var lastQuery = '';

    function setOpen(isOpen) {
      root.classList.toggle('is-open', isOpen);
      modal.hidden = !isOpen;
      openBtn.setAttribute('aria-expanded', String(isOpen));

      if (isOpen) {
        document.body.style.overflow = 'hidden';
        releaseFocus = trapFocus(modal, input);
        input.focus();
      } else {
        document.body.style.overflow = '';
        if (releaseFocus) {
          releaseFocus();
          releaseFocus = null;
        }
        hideResults();
        openBtn.focus();
      }
    }

    function hideResults() {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    }

    function renderResults(items) {
      if (!items.length) {
        hideResults();
        if (status) status.textContent = '';
        return;
      }

      results.innerHTML = items
        .map(function (item, index) {
          var type = item.type || 'result';
          var title = item.title || item.text || '';
          var url = item.url || '#';
          return (
            '<a class="aero-component-header-search__result" role="option" href="' +
            url +
            '" data-aero-header-search-result data-index="' +
            index +
            '">' +
            '<span class="aero-component-header-search__result-type">' +
            type +
            '</span>' +
            '<span>' +
            title +
            '</span></a>'
          );
        })
        .join('');

      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (status) {
        status.textContent = items.length + ' results';
      }
    }

    function fetchSuggestions(query) {
      if (!query.trim()) {
        hideResults();
        return;
      }

      var url =
        '/search/suggest.json?q=' +
        encodeURIComponent(query) +
        '&resources[type]=product,page,article&resources[limit]=' +
        SUGGEST_LIMIT;

      fetch(url)
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var items = [];
          var resources = data.resources && data.resources.results;

          if (resources) {
            ['products', 'pages', 'articles'].forEach(function (key) {
              var group = resources[key] || [];
              group.forEach(function (entry) {
                items.push({
                  type: key.replace(/s$/, ''),
                  title: entry.title,
                  url: entry.url,
                });
              });
            });
          }

          renderResults(items.slice(0, SUGGEST_LIMIT));
        })
        .catch(function () {
          hideResults();
        });
    }

    function scheduleFetch(query) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        if (query === lastQuery) return;
        lastQuery = query;
        fetchSuggestions(query);
      }, DEBOUNCE_MS);
    }

    function setActiveResult(index) {
      var links = results.querySelectorAll('[data-aero-header-search-result]');
      links.forEach(function (link, i) {
        link.classList.toggle('is-active', i === index);
      });
      activeIndex = index;
    }

    openBtn.addEventListener('click', function () {
      setOpen(true);
    });

    closeBtn.addEventListener('click', function () {
      setOpen(false);
    });

    overlay.addEventListener('click', function () {
      setOpen(false);
    });

    input.addEventListener('input', function () {
      scheduleFetch(input.value);
    });

    input.addEventListener('keydown', function (event) {
      var links = results.querySelectorAll('[data-aero-header-search-result]');

      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
        return;
      }

      if (results.hidden || !links.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        var next = activeIndex + 1 >= links.length ? 0 : activeIndex + 1;
        setActiveResult(next);
        links[next].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        var prev = activeIndex - 1 < 0 ? links.length - 1 : activeIndex - 1;
        setActiveResult(prev);
        links[prev].focus();
      } else if (event.key === 'Enter' && activeIndex >= 0 && links[activeIndex]) {
        event.preventDefault();
        window.location.href = links[activeIndex].href;
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && root.classList.contains('is-open')) {
        setOpen(false);
      }
    });

    form.addEventListener('submit', function () {
      setOpen(false);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-aero-header-search]').forEach(initHeaderSearch);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
